import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Compass, Infinity as InfinityIcon, Sparkles, Wind, Menu, X, ArrowRight, Ban, Flame, Scale, Swords, Shield, Gavel } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StatsBand } from "@/components/StatsBand";
import { CocoonMark } from "@/components/CocoonMark";
import { HeroSpark } from "@/components/HeroSpark";
import { CommentCaMarche } from "@/components/CommentCaMarche";
import { MonthShortcuts } from "@/components/MonthShortcuts";
import { CuriositeSection } from "@/components/CuriositeSection";
import { BoucleCuriosite } from "@/components/BoucleCuriosite";
import { TarifsApercu } from "@/components/TarifsApercu";
import { Pourquoi } from "@/components/Pourquoi";
import { CardCarousel } from "@/components/CardCarousel";
import { UniversHub } from "@/components/UniversHub";
import { CercleTeaser } from "@/components/CercleTeaser";
import { CapsulesTeaser } from "@/components/CapsulesTeaser";
import { TeaserDuel } from "@/components/TeaserDuel";
import { ComingSoonTeaser } from "@/components/ComingSoonTeaser";
import { Newsletter } from "@/components/Newsletter";
import { InstallButton } from "@/components/InstallButton";
import { MobileStickyCTA } from "@/components/MobileStickyCTA";
import { SocialProof } from "@/components/SocialProof";
import { OnlineNow } from "@/components/OnlineNow";
import { Footer } from "@/components/Footer";
import { FeaturedEpisode } from "@/components/FeaturedEpisode";

const HERO_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/yrai43tk_gemini_generated_video_92777b7b.mp4";
const MAITRE_DISCIPLE =
  "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/f64cb24005af5f525f0a3895588dc2fc1903119793de6da99d7161164dd42f71.jpeg";

const PILLAR_BACKGROUNDS = [
  "/valeurs/man_frontiere.jpg",
  "/valeurs/man_evaluation.jpg",
  "/valeurs/man_destination.jpg",
  "/valeurs/man_elite.jpg",
];

const PROCES_IMG = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/07f7cbefa733e3fc9bd8edcf18b45bc03de126dacede3264e2d97aabc57b9937.jpeg";
const DOUTE_IMG = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/51829eef3b80f9aae447a5a922d977ec0ed96f47bea9582f5ea0a768291bddd2.jpeg";
const PROCES_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/eib6e99b_gemini_generated_video_d4cfc4e2.mp4";
const DOUTE_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/zhn93q78_Le_Feu_du_Doute_Bouddha_prend.mp4";
const PROCES_PERSONAS = [{ Icon: Swords, color: "red" }, { Icon: Shield, color: "teal" }, { Icon: Gavel, color: "gold" }];
const DOUTE_PERSONAS = [{ Icon: Flame, color: "red" }];


export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const pillars = t("pillars", { returnObjects: true });

  const openTool = (tab) => { localStorage.setItem("coccon_pending_tab", tab); navigate("/atelier"); };
  const arenaPills = [
    { tab: "deuxVoies", Icon: Swords, label: t("landing.duel.name") },
    { tab: "doute", Icon: Flame, label: t("landing.doute.name") },
    { tab: "proces", Icon: Scale, label: t("landing.proces.name") },
  ];

  // Deep-link: scroll to #zone-xxx anchor on load (content lazy-loads, so retry)
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#zone-")) return;
    const id = hash.slice(1);
    let tries = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
      if (tries++ < 12) setTimeout(tick, 300);
    };
    setTimeout(tick, 400);
  }, []);

  const navLinks = [
    { to: "/manifeste", key: "manifeste", testId: "nav-manifeste-btn" },
    { to: "/chemin-de-soie", key: "chemin", testId: "nav-chemin-btn" },
    { to: "/ecole-cocoon", key: "ecole", testId: "nav-ecole-btn" },
    { to: "/echo", key: "echo", testId: "nav-echo-btn" },
    { to: "/enfants", key: "enfants", testId: "nav-enfants-btn" },
    { to: "/tarifs", key: "tarifs", testId: "nav-tarifs-btn" },
    { to: "/derive", key: "derive", testId: "nav-derive-btn" },
  ];

  const zone = (k) => ({ eyebrow: t(`landing.zones.${k}.eyebrow`), title: t(`landing.zones.${k}.title`) });

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-x-clip">
      <div className="grain-overlay" aria-hidden />
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.4 }}
        className="fixed top-[72px] inset-x-0 z-40 flex justify-center px-4 pointer-events-none"
        data-testid="online-now-banner"
      >
        <OnlineNow />
      </motion.div>
      <div className="amber-glow" aria-hidden style={{ top: "-12%", left: "50%", transform: "translateX(-50%)", width: "920px", height: "720px" }} />
      <div className="amber-glow" aria-hidden style={{ top: "48%", left: "-14%", width: "680px", height: "680px", opacity: 0.65 }} />
      <div className="amber-glow" aria-hidden style={{ top: "78%", right: "-14%", left: "auto", width: "640px", height: "640px", opacity: 0.55 }} />
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-2.5 whitespace-nowrap" data-testid="brand-logo">
            <img
              src="/cocoon-logo-glow.png"
              alt="COCOON MIND"
              data-testid="brand-logo-mark"
              className="h-9 sm:h-10 w-auto select-none"
              style={{ filter: "drop-shadow(0 0 8px hsl(28 96% 56% / 0.30))" }}
            />
            <span className="font-display text-lg sm:text-2xl tracking-tight"><CocoonMark withMind /></span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-5">
            <LanguageSwitcher />
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} data-testid={l.testId} className="text-sm text-foreground/60 hover:text-primary transition-colors whitespace-nowrap">{t(`nav.${l.key}`)}</Link>
            ))}
            <Link to="/entrer" data-testid="nav-enter-btn" className="btn-frost-orange text-sm font-semibold px-6 py-2 rounded-full whitespace-nowrap">{t("nav.entrer")}</Link>
          </nav>

          {/* Mobile controls */}
          <div className="flex md:hidden items-center gap-2">
            <LanguageSwitcher />
            <Link to="/entrer" data-testid="nav-enter-btn-mobile" className="btn-frost-orange text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap">{t("nav.entrer")}</Link>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              aria-expanded={menuOpen}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 glass" data-testid="mobile-menu">
            <nav className="px-4 py-3 flex flex-col">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => setMenuOpen(false)}
                  className="py-3 border-b border-white/5 text-foreground/75 hover:text-primary transition-colors text-base"
                >
                  {t(`nav.${l.key}`)}
                </Link>
              ))}
              <Link
                to="/philosophie"
                data-testid="nav-philosophie-btn-mobile"
                onClick={() => setMenuOpen(false)}
                className="py-3 border-b border-white/5 text-foreground/75 hover:text-primary transition-colors text-base"
              >
                {t("landing.philosophieCta")}
              </Link>
              <Link
                to="/qui-parle-aux-ia"
                data-testid="nav-qui-parle-ia-btn-mobile"
                onClick={() => setMenuOpen(false)}
                className="py-3 border-b border-white/5 text-foreground/75 hover:text-primary transition-colors text-base"
              >
                {t("nav.quiParleIA")}
              </Link>
              <Link
                to="/memoire"
                data-testid="nav-echo-lueurs-btn-mobile"
                onClick={() => setMenuOpen(false)}
                className="py-3 border-b border-white/5 text-foreground/75 hover:text-primary transition-colors text-base"
              >
                {t("nav.echoLueurs")}
              </Link>
              <Link
                to="/faq"
                data-testid="nav-faq-btn-mobile"
                onClick={() => setMenuOpen(false)}
                className="py-3 border-b border-white/5 text-foreground/75 hover:text-primary transition-colors text-base"
              >
                {t("nav.faq")}
              </Link>
              <div className="pt-4 pb-1 flex flex-col gap-4">
                <InstallButton testId="nav-install-btn-mobile" />
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* ===== HERO (inchangé) ===== */}
      <section id="hero-anchor" className="relative min-h-[85vh] md:min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <video
            src={HERO_VIDEO}
            autoPlay loop muted playsInline
            data-testid="hero-cinematic-video"
            poster={MAITRE_DISCIPLE}
            className="w-full h-full object-cover opacity-70 hero-video-fade"
          />
          <div className="absolute inset-0 bg-background/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/55 to-background/45" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-transparent to-transparent" />
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "radial-gradient(circle at 50% 46%, hsl(28 96% 56% / 0.26), hsl(24 95% 52% / 0.10) 38%, transparent 60%)",
              animation: "cinematic-glow-pulse 4s ease-in-out infinite",
            }}
          />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-20 sm:py-28 w-full">
          <motion.img
            initial={{ opacity: 0, scale: 0.8, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 1.3, ease: "easeOut" }}
            src="/cocoon-logo-glow.png"
            alt="COCOON MIND"
            data-testid="hero-logo"
            className="relative z-10 mx-auto mb-3 sm:mb-6 h-40 sm:h-72 lg:h-[22rem] w-auto select-none"
            style={{ filter: "drop-shadow(0 0 44px hsl(28 96% 56% / 0.42))" }}
          />
          <motion.h2
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.15 }}
            className="relative z-10 font-display text-center tracking-tight leading-none mb-4 sm:mb-8"
            style={{ fontSize: "clamp(3.25rem, 15vw, 12rem)" }}
          ><CocoonMark letterClassName="cocoon-hero" withMind /></motion.h2>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
            className="text-primary/90 tracking-[0.3em] uppercase text-xs mb-4 sm:mb-6 text-center">{t("landing.eyebrow")}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }}
            className="font-display text-gradient-orange text-center mx-auto text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">
            {t("landing.heroA")}<span className="italic text-primary">{t("landing.heroHighlight")}</span>{t("landing.heroB")}
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.25 }}
            className="mt-5 sm:mt-7 max-w-2xl mx-auto text-center space-y-2" data-testid="hero-poem">
            {(t("landing.heroPoem", { returnObjects: true }) || []).map((line, i) => (
              <p key={i} className="font-display italic text-foreground/80 text-lg sm:text-2xl leading-relaxed">{line}</p>
            ))}
            <p className="mt-3 sm:mt-4 font-display text-primary/90 text-lg sm:text-2xl leading-relaxed" data-testid="hero-mission">{t("landing.heroMission")}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}
            className="mt-6 sm:mt-9 max-w-xl mx-auto grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="hero-nots">
            {(t("landing.heroNots", { returnObjects: true }) || []).map((n, i) => (
              <span key={i} data-testid={`hero-not-${i}`}
                className="flex items-center justify-center gap-1.5 h-12 px-2 rounded-xl border border-primary/25 bg-primary/[0.06] text-xs sm:text-sm text-foreground/80 text-center leading-tight">
                <Ban size={13} className="flex-shrink-0 text-primary/80" /> {n}
              </span>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }} className="mt-6 sm:mt-10 flex justify-center">
            <Link
              to="/entrer"
              data-testid="hero-cta-btn"
              className="btn-frost-orange group inline-flex items-center gap-4 pl-8 pr-3 py-3 rounded-full font-semibold text-base sm:text-lg"
            >
              <span>{t("landing.cta")}</span>
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[hsl(22_62%_14%_/_0.14)] transition-transform duration-500 group-hover:rotate-45">
                <Compass size={20} strokeWidth={2.2} />
              </span>
            </Link>
          </motion.div>
          <HeroSpark />
        </div>
      </section>

      <section className="relative px-6 -mt-10 mb-8">
        <StatsBand />
      </section>
      <FeaturedEpisode />
      <CommentCaMarche />

      {/* ===== Les expériences dialectiques — mises en avant, tôt sur la page ===== */}
      <section className="max-w-6xl mx-auto px-6 pt-12 sm:pt-16 pb-2 text-center" data-testid="duel-group-header">
        <span className="inline-block text-[0.7rem] uppercase tracking-[0.28em] text-primary/70 border border-primary/25 rounded-full px-3 py-1 mb-4">{t("landing.duelGroup.eyebrow")}</span>
        <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-tight mb-3">{t("landing.duelGroup.title")}</h2>
        <p className="text-foreground/60 max-w-xl mx-auto leading-relaxed">{t("landing.duelGroup.subtitle")}</p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3" data-testid="arena-pills">
          {arenaPills.map(({ tab, Icon, label }) => (
            <button
              key={tab}
              onClick={() => openTool(tab)}
              data-testid={`arena-pill-${tab}`}
              className="group inline-flex items-center gap-2.5 rounded-full border border-primary/30 bg-primary/[0.06] pl-4 pr-5 py-2.5 text-sm font-semibold text-foreground/85 hover:bg-primary/15 hover:border-primary/50 hover:text-primary transition-all duration-300 hover:-translate-y-0.5"
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/15 text-primary group-hover:scale-110 transition-transform">
                <Icon size={15} strokeWidth={1.8} />
              </span>
              {label}
            </button>
          ))}
        </div>
      </section>
      <TeaserDuel />
      <ComingSoonTeaser ns="doute" Icon={Flame} image={DOUTE_IMG} video={DOUTE_VIDEO} personas={DOUTE_PERSONAS} testId="doute-teaser" navTab="doute" />
      <ComingSoonTeaser ns="proces" Icon={Scale} image={PROCES_IMG} video={PROCES_VIDEO} personas={PROCES_PERSONAS} testId="proces-teaser" navTab="proces" />

      {/* ===== L'Étincelle du Cocoon Mind (la curiosité) ===== */}
      <CuriositeSection />

      {/* ===== La Boucle de la Curiosité ===== */}
      <BoucleCuriosite />

      {/* ===== Le Cercle de réflexion ===== */}
      <CercleTeaser />

      {/* ===== Les Capsules de curiosité ===== */}
      <CapsulesTeaser />

      {/* ===== Raccourcis du mois : débat + conférence (rendez-vous) ===== */}
      <MonthShortcuts />

      {/* ===== Les 6 univers (hub léger) ===== */}
      <UniversHub />


      {/* ===== Notre ADN — storytelling de marque ===== */}
      <section className="relative px-6 mb-12">
        <Pourquoi />
      </section>

      <section className="max-w-6xl mx-auto px-6 pt-6 pb-14 relative z-10">
        <h2 className="font-display text-gradient-orange text-center mx-auto text-3xl sm:text-4xl italic mb-16 max-w-md">{t("landing.manifestoTitle")}</h2>
        <div className="max-w-4xl mx-auto">
          <CardCarousel
            testId="pillars-carousel"
            interval={10000}
            slides={pillars.map((p, i) => {
              const bg = PILLAR_BACKGROUNDS[i % PILLAR_BACKGROUNDS.length];
              return (
                <article className="relative overflow-hidden glass frame-pulse rounded-3xl p-8 md:p-12 min-h-[500px] sm:min-h-[440px] flex flex-col justify-center" data-testid={`pillar-${i}`}>
                  <div className="absolute inset-0" aria-hidden="true">
                    <img src={bg} alt="" data-testid={`pillar-bg-${i}`} className="w-full h-full object-cover opacity-70" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/20" />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/35 to-transparent" />
                  </div>
                  <div className="relative z-10">
                    <h3 className="font-display text-2xl md:text-3xl mb-4">{p.title}</h3>
                    <p className="text-foreground/70 leading-[1.9] text-base md:text-lg max-w-xl">{p.text}</p>
                  </div>
                </article>
              );
            })}
          />
        </div>
      </section>

      <TarifsApercu />

      {/* CTA vers la philosophie complète */}
      <section className="max-w-3xl mx-auto px-6 pb-24 text-center">
        <p className="text-foreground/55 text-sm mb-6 max-w-md mx-auto leading-relaxed">{t("landing.philosophie.intro")}</p>
        <Link to="/philosophie" data-testid="philosophie-cta" className="btn-frost-orange group inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-base sm:text-lg">
          {t("landing.philosophieCta")}
          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </section>

      <Newsletter />

      <Footer />

      {/* Mobile-only sticky conversion bar + spacer so it never hides footer content */}
      <div className="h-20 md:hidden" aria-hidden />
      <MobileStickyCTA />
      <SocialProof />
    </div>
  );
}
